import { Form, Input, Select } from 'antd';

import { apiFetch } from '@/api/client';
import { employeesKey, useUsers, usersKey, type Employee } from '@/api/masters';
import { useApiMutation } from '@/api/mutations';
import { FormDrawer } from '@/components/FormDrawer';
import type { IssuedPassword } from '@/components/OneTimePasswordModal';
import { Notice } from '@/components/PageHeading';

import { loginProblem, loginRequest, usernameProblem, type LoginChoice } from './login';

/** What the server sends back: the updated row, and the password only when it minted the account. */
interface EmployeeLoginResult {
  employee: Employee;
  temporaryPassword: string | null;
}

interface LoginFormValues {
  mode: 'existing' | 'new';
  userId?: number;
  username?: string;
}

/**
 * Gives a member of staff a way to sign in.
 *
 * <p>
 * <strong>Two ways in, because both are real.</strong> Somebody re-joining the shop, or a partner
 * entered as a user before the staff master existed, already has an account — minting a second one
 * for the same person spends a second seat and leaves a shop wondering which login is the live one.
 * Everyone else needs one created.
 * </p>
 *
 * <p>
 * <strong>Roles are deliberately not here.</strong> `NewEmployeeLogin` treats an account with no
 * roles as a real state — the seeded `nikhil` exists to make it visible — and per-branch role
 * assignment is a table that already exists on the Users screen. A second copy of it here would be
 * two presentations of one thing, and the neglected one is always the second.
 * </p>
 *
 * <p>
 * <strong>Every account is offered, including ones already linked, and that is a known gap.</strong>
 * The link is one-to-one and the server answers 409 on a taken account, so the honest thing would be
 * to leave those out — but `UserDto` does not name the employee it belongs to, and the FK lives on
 * the employee side, so the client cannot answer "is this account taken" without paging the whole
 * staff register. Filtering on the page the screen happens to have loaded would hide *some* of them
 * and quietly mislead about the rest. The 409 is legible, and closing this properly is a field on
 * `UserDto`.
 * </p>
 *
 * <p>
 * <strong>The temporary password comes back exactly once</strong>, so this hands it straight to the
 * shared modal rather than a toast. It is null when an existing account was attached, which is not
 * an omission: that person already has a password and this call did not change it.
 * </p>
 */
export function EmployeeLoginDrawer({
  employee,
  onClose,
  onIssued,
}: {
  /** The staff member being given a login, or null when the drawer is shut. */
  employee: Employee | null;
  onClose: () => void;
  /** Raised only when this call minted an account, with the value that is shown once. */
  onIssued: (issued: IssuedPassword) => void;
}) {
  const [form] = Form.useForm<LoginFormValues>();

  /*
    Watched off the form rather than held beside it. A `value`/`onChange` pair of this component's
    own would override the ones `Form.Item` injects, so the field the form submits would stay at
    its initial value however the control looked — picking "attach an account" would have posted a
    username that was never typed and failed silently. Same pattern as `AttributesPage`.
  */
  const mode = Form.useWatch('mode', form) ?? 'new';

  // Inactive accounts included on purpose: somebody re-joining the shop is exactly the case for
  // attaching rather than minting, and switching the account back on is the Users screen's job.
  const users = useUsers(true);

  const link = useApiMutation<EmployeeLoginResult, LoginChoice>({
    mutationFn: (choice) =>
      apiFetch<EmployeeLoginResult>(`/api/employee/${employee?.id}/user`, {
        method: 'POST',
        body: loginRequest(choice),
      }),
    success: (result) =>
      result.temporaryPassword === null
        ? `${result.employee.fullName} now signs in as ${result.employee.username}.`
        : `Account created for ${result.employee.fullName}.`,
    // Both lists change: the staff row gains a username, and a minted account is a new user.
    invalidates: [employeesKey, usersKey],
    onSuccess: (result) => {
      if (result.temporaryPassword !== null && result.employee.username !== null) {
        onIssued({ username: result.employee.username, password: result.temporaryPassword });
      }

      onClose();
    },
  });

  if (employee === null) {
    return null;
  }

  return (
    <FormDrawer<LoginFormValues>
      title={`Give ${employee.fullName} a login`}
      subtitle="A login takes a seat on the plan, so most staff never have one."
      open
      onClose={onClose}
      form={form}
      initialValues={{ mode: 'new' }}
      submitText="Give login"
      submitting={link.isPending}
      onSubmit={(values) => {
        const choice: LoginChoice =
          values.mode === 'existing'
            ? { kind: 'existing', userId: values.userId ?? null }
            : { kind: 'new', username: values.username ?? '' };

        // The client mirrors the server rather than trusting the form to have run: `loginRequest`
        // refuses to build a body that is not ready, so a bad one can never leave here.
        if (loginProblem(choice) !== null) {
          return;
        }

        void link.mutateAsync(choice).catch(() => undefined);
      }}
    >
      <Form.Item name="mode" label="How they sign in">
        <Select
          options={[
            { value: 'new', label: 'Create a new login' },
            { value: 'existing', label: 'Attach an account they already have' },
          ]}
        />
      </Form.Item>

      {mode === 'new' ? (
        <Form.Item
          name="username"
          label="Username"
          rules={[
            {
              validator: (_rule, value: string | undefined) => {
                const problem = usernameProblem(value ?? '');

                return problem === undefined
                  ? Promise.resolve()
                  : Promise.reject(new Error(problem));
              },
            },
          ]}
          extra="Fixed once created, like every username. They choose their own password as they first sign in."
        >
          <Input autoComplete="off" />
        </Form.Item>
      ) : (
        <Form.Item
          name="userId"
          label="Account"
          rules={[{ required: true, message: 'Choose the account this person signs in with.' }]}
        >
          <Select
            showSearch
            optionFilterProp="label"
            placeholder="Search by name or username"
            loading={users.isLoading}
            options={(users.data ?? []).map((user) => ({
              value: user.id,
              label: `${user.fullName} · ${user.username}`,
            }))}
          />
        </Form.Item>
      )}

      <Notice tone="info" title="This does not grant them anything yet">
        A new account signs in and can do nothing until it is given a role. Roles are held per
        branch and are assigned on the Users screen.
      </Notice>
    </FormDrawer>
  );
}
